import { useEffect, useState } from "react";
import { Clipboard, Pencil } from "lucide-react";
import { toast } from "sonner";

import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/auth";

/**
 * Chase-template controls: a "Copy chase template" button + a pencil edit
 * button that opens a dialog letting Kate/Bella save their preferred copy.
 *
 * Self-contained: fetches its own state from GET /admin/chase-template and
 * saves via PUT. Renders nothing until `visible` is true so the buttons
 * only show up when the "Needs chasing" filter chip is active.
 */
export function ChaseEditor({ visible }) {
  const [template, setTemplate] = useState("");
  const [isDefault, setIsDefault] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/chase-template");
        setTemplate(data?.template || "");
        setIsDefault(Boolean(data?.is_default));
      } catch {
        setTemplate("");
        setIsDefault(true);
      }
    })();
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(template || "");
      toast.success(
        isDefault
          ? "Chase template copied — paste into your email client"
          : "Bella's chase template copied — paste into your email client"
      );
    } catch {
      toast.error("Clipboard blocked by browser — copy manually from the console");
      // eslint-disable-next-line no-console
      console.info(template);
    }
  };

  const openEditor = () => {
    setDraft(template || "");
    setEditorOpen(true);
  };

  const save = async () => {
    if (draft.trim().length < 10) {
      toast.error("Template is too short to save");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/admin/chase-template", { template: draft });
      setTemplate(data.template);
      setIsDefault(false);
      setEditorOpen(false);
      toast.success("Chase template saved");
    } catch {
      toast.error("Couldn't save the chase template");
    } finally {
      setSaving(false);
    }
  };

  if (!visible) return null;

  return (
    <>
      <button
        type="button"
        data-testid="admin-copy-chase-template"
        onClick={copy}
        className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3 py-1.5 text-xs font-medium text-[#2A2724] transition-colors hover:bg-[#EDEAE4]"
      >
        <Clipboard className="h-3.5 w-3.5" />
        Copy chase template
        {!isDefault && (
          <span
            data-testid="admin-chase-custom-dot"
            title="Custom template saved"
            className="inline-block h-1.5 w-1.5 rounded-full bg-gold"
          />
        )}
      </button>
      <button
        type="button"
        data-testid="admin-edit-chase-template"
        onClick={openEditor}
        title="Edit chase template"
        className="inline-flex items-center gap-1 rounded-full border border-black/10 bg-white px-2.5 py-1.5 text-xs text-[#6B6B6B] transition-colors hover:bg-[#EDEAE4]"
      >
        <Pencil className="h-3.5 w-3.5" />
      </button>

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent
          className="max-w-xl"
          data-testid="admin-chase-editor-dialog"
        >
          <DialogHeader>
            <DialogTitle>Chase email template</DialogTitle>
            <DialogDescription>
              This copy powers the <em>Copy chase template</em> button. Use{" "}
              <code className="rounded bg-[#F7F5F2] px-1 py-0.5 text-[0.7rem]">{"{{ first_name }}"}</code>,{" "}
              <code className="rounded bg-[#F7F5F2] px-1 py-0.5 text-[0.7rem]">{"{{ business_name }}"}</code>,{" "}
              <code className="rounded bg-[#F7F5F2] px-1 py-0.5 text-[0.7rem]">{"{{ sign_url }}"}</code>{" "}
              as placeholders you'll fill in per-founder before sending.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="admin-chase-editor-textarea"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={14}
            className="font-mono text-xs"
          />
          <DialogFooter className="gap-2">
            <button
              type="button"
              data-testid="admin-chase-editor-cancel"
              onClick={() => setEditorOpen(false)}
              className="rounded-full border border-black/10 px-4 py-2 text-xs font-medium text-[#6B6B6B] hover:bg-[#EDEAE4]"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="admin-chase-editor-save"
              onClick={save}
              disabled={saving}
              className="rounded-full bg-gold px-4 py-2 text-xs font-medium text-black hover:bg-[#AA8627] disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save template"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
